import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Modal, Typography } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";

import {
  getRegistrationDetails,
  isLoading,
  getActiveApplication,
} from "../../selectors";

import {
  clearRegistrationDetails,
  displaySecret,
  getRegistrationsForApplication,
} from "../../actions";

import OidcDetails from "./OidcDetails";
import * as cfg from "./../../config";
import { RegistrationProtocols } from "../../models/registrationProtocols";
import { FullPageLoader } from "@authzsvc/common-react-components";

const { Title, Paragraph } = Typography;

class RegistrationDetails extends React.Component {
  static propTypes = {
    details: PropTypes.object,
    loading: PropTypes.bool,
    activeApplication: PropTypes.object,
    actions: PropTypes.shape({
      getRegistrationsForApplication: PropTypes.func,
      clearRegistrationDetails: PropTypes.func,
      displaySecret: PropTypes.func,
    }),
  };

  state = {
    secret: undefined,
    loadingSecret: false,
  };

  async componentDidMount() {
    await this.getRegistrationSecret();
  }

  async getRegistrationSecret() {
    const { details, actions } = this.props;

    if (
      details.providerIdentifier === "openid_connect" &&
      details.registration?.clientType !== "public"
    ) {
      this.setState({ loadingSecret: true });
      try {
        const response = await actions.displaySecret(details.registrationId);
        const secret = response?.payload?.data?.secret;
        if (secret) this.setState({ secret });
      } catch (error) {
        console.error("Error fetching registration secret:", error);
      } finally {
        this.setState({ loadingSecret: false });
      }
    }
  }

  handleModalClose = async () => {
    const { actions, activeApplication } = this.props;
    await actions.getRegistrationsForApplication(activeApplication.id);
    actions.clearRegistrationDetails();
  };

  displayDetails = (registration) => {
    if (registration.providerIdentifier === RegistrationProtocols.SAML) {
      const metadata = `${cfg.keycloakUrl}/realms/${cfg.keycloakRealm}/protocol/saml/descriptor`;
      return (
        <>
          <Paragraph>
            Now you can install the{" "}
            <a href={metadata} target="_blank" rel="noopener noreferrer">
              CERN SSO Identity Provider metadata
            </a>{" "}
            in your application. You can find further CERN SSO configuration
            details in our{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://auth.docs.cern.ch/user-documentation/saml/config/"
            >
              User Docs
            </a>
            .
          </Paragraph>
        </>
      );
    }

    if (registration.providerIdentifier === RegistrationProtocols.OIDC) {
      const oidcRegistrationDetails = registration.registration;
      return (
        <>
          {oidcRegistrationDetails.clientType !== "public" && (
            <>
              <Paragraph>
                Make sure you store the following client ID and secret safely.
              </Paragraph>
              <OidcDetails
                clientId={oidcRegistrationDetails.clientId}
                secret={this.state.secret}
              />
            </>
          )}
          {oidcRegistrationDetails.clientType === "public" && (
            <OidcDetails clientId={oidcRegistrationDetails.clientId} />
          )}
          <Paragraph>
            You can find CERN SSO configuration details in our{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://auth.docs.cern.ch/user-documentation/oidc/config/"
            >
              User Docs
            </a>
            .
          </Paragraph>
        </>
      );
    }

    return <Paragraph>Unknown authentication provider</Paragraph>;
  };

  render() {
    const { details, loading } = this.props;
    const { loadingSecret } = this.state;

    if (loading || loadingSecret) {
      return <FullPageLoader />;
    }

    if (Object.keys(details).length === 0) {
      return null;
    }

    return (
      <Modal
        title="Successful Registration"
        open
        onCancel={this.handleModalClose}
        footer={null}
        centered
        width={700}
        closable
      >
        <Title level={4}>
          <CheckCircleTwoTone twoToneColor="#52c41a" />
          <span style={{ marginLeft: 8 }} />
          Your Application has been registered
        </Title>
        {this.displayDetails(details)}

        <Paragraph>
          For help securing your application, take a look at the{" "}
          <a
            rel="noopener noreferrer"
            target="_blank"
            href="https://auth.docs.cern.ch/applications/application-configuration/"
          >
            Documentation
          </a>
          .
        </Paragraph>
      </Modal>
    );
  }
}

export default connect(
  (state) => ({
    details: getRegistrationDetails(state),
    activeApplication: getActiveApplication(state).application,
    loading: isLoading(state, "registerApplication"),
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        clearRegistrationDetails,
        getRegistrationsForApplication,
        displaySecret,
      },
      dispatch,
    ),
  }),
)(RegistrationDetails);
